import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  FAILURE_TYPES,
  listQuarantineBins,
  recordDamageReport,
} from '@/lib/api/warranty.js';
import { listUsers } from '@/lib/api/users.js';
import { SupplierSelect } from '@/components/suppliers/SupplierSelect.js';
import { parseApiError } from '@/lib/api/errors.js';
import { toastError, toastSuccess } from '@/lib/toast.js';

export function ReportDamageModal({
  open,
  itemId,
  itemName,
  serials,
  onClose,
}: {
  open: boolean;
  itemId: string;
  itemName: string;
  serials?: Array<Record<string, unknown>> | undefined;
  onClose: () => void;
}) {
  const [failureType, setFailureType] = useState('shipping_damage');
  const [reason, setReason] = useState('');
  const [quantity, setQuantity] = useState('1');
  const [supplierId, setSupplierId] = useState('');
  const [serialId, setSerialId] = useState('');
  const [binId, setBinId] = useState('');
  const [attributedTo, setAttributedTo] = useState('');
  const [disposition, setDisposition] = useState('quarantine');
  const binsQuery = useQuery({ queryKey: ['quarantine-bins'], queryFn: listQuarantineBins, enabled: open });
  const usersQuery = useQuery({ queryKey: ['users'], queryFn: listUsers, enabled: open && failureType === 'tech_damage' });
  const availableSerials = (serials ?? []).filter((serial) => {
    const status = String(serial.status ?? '');
    return status === 'in_stock' || status === 'reserved' || status === 'sold';
  });

  const mutation = useMutation({
    mutationFn: () =>
      recordDamageReport({
        inventoryItemId: itemId,
        inventorySerialId: serialId || undefined,
        quantity: serialId ? 1 : Math.max(1, Number(quantity) || 1),
        failureType,
        failureReason: reason || undefined,
        supplierId: supplierId || undefined,
        quarantineBinId: binId || undefined,
        disposition,
        attributedTo: failureType === 'tech_damage' ? attributedTo || undefined : undefined,
        partName: itemName,
      }),
    onSuccess: () => {
      toastSuccess('Damage recorded. The part is out of sellable stock.');
      onClose();
    },
    onError: (error) => toastError(parseApiError(error).message),
  });

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/40 p-4">
      <form
        className="w-full max-w-lg space-y-3 rounded-2xl bg-white p-6 shadow-2xl"
        onSubmit={(event) => {
          event.preventDefault();
          mutation.mutate();
        }}
      >
        <h3 className="text-lg font-semibold">Report damaged part</h3>
        <p className="text-sm text-slate-600">
          {itemName} leaves sellable stock immediately. A manager can reclassify it later.
        </p>
        <label className="block text-sm font-medium">
          Failure
          <select className="mt-1 w-full rounded-xl border px-3 py-2" value={failureType} onChange={(e) => setFailureType(e.target.value)}>
            {FAILURE_TYPES.map((type) => (
              <option key={type.value} value={type.value}>{type.label}</option>
            ))}
          </select>
        </label>
        {availableSerials.length > 0 ? (
          <label className="block text-sm font-medium">
            Device serial
            <select className="mt-1 w-full rounded-xl border px-3 py-2" value={serialId} onChange={(e) => setSerialId(e.target.value)}>
              <option value="">Not a specific serial</option>
              {availableSerials.map((serial) => (
                <option key={String(serial.id)} value={String(serial.id)}>
                  {String(serial.imei || serial.serialNumber || serial.id)} · {String(serial.status)}
                </option>
              ))}
            </select>
          </label>
        ) : null}
        {!serialId ? (
          <label className="block text-sm font-medium">
            Quantity
            <input type="number" min="1" className="mt-1 w-full rounded-xl border px-3 py-2" value={quantity} onChange={(e) => setQuantity(e.target.value)} />
          </label>
        ) : null}
        <label className="block text-sm font-medium">
          Supplier
          <div className="mt-1">
            <SupplierSelect value={supplierId} onChange={setSupplierId} />
          </div>
        </label>
        <label className="block text-sm font-medium">
          Disposition
          <select className="mt-1 w-full rounded-xl border px-3 py-2" value={disposition} onChange={(e) => setDisposition(e.target.value)}>
            <option value="quarantine">Quarantine</option>
            <option value="rma">RMA</option>
            <option value="write_off">Write off</option>
          </select>
        </label>
        <label className="block text-sm font-medium">
          Bin
          <select className="mt-1 w-full rounded-xl border px-3 py-2" value={binId} onChange={(e) => setBinId(e.target.value)}>
            <option value="">Unassigned</option>
            {(binsQuery.data ?? []).filter((bin) => bin.active).map((bin) => (
              <option key={bin.id} value={bin.id}>{bin.name}</option>
            ))}
          </select>
        </label>
        {failureType === 'tech_damage' ? (
          <label className="block text-sm font-medium">
            Attributed technician
            <select className="mt-1 w-full rounded-xl border px-3 py-2" value={attributedTo} onChange={(e) => setAttributedTo(e.target.value)}>
              <option value="">Unknown</option>
              {(usersQuery.data ?? []).map((user) => (
                <option key={user.id} value={user.id}>{user.firstName} {user.lastName}</option>
              ))}
            </select>
          </label>
        ) : null}
        <textarea className="w-full rounded-xl border px-3 py-2" placeholder="What happened" value={reason} onChange={(e) => setReason(e.target.value)} />
        <div className="flex justify-end gap-2">
          <button type="button" className="rounded-xl border px-4 py-2 text-sm font-semibold" onClick={onClose}>Cancel</button>
          <button type="submit" className="rounded-xl bg-primary px-4 py-2 text-sm font-semibold text-white" disabled={mutation.isPending}>
            {mutation.isPending ? 'Saving…' : 'Record damage'}
          </button>
        </div>
      </form>
    </div>
  );
}
